import { useSelector } from 'react-redux';
import { useState, useEffect } from 'react';
import { Button, Modal } from 'antd';
import { DragOutlined, FullscreenOutlined, UpOutlined, DownOutlined } from '@ant-design/icons';
import GridLayout, { WidthProvider, type LayoutItem } from 'react-grid-layout/legacy';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import HierarchicalChart from '../../components/charts/HierarchicalChart';
import TrainerDashboard from '../../components/dashboard/trainerDashboard';
import SuperAdminDashboardWidgets from '../superAdminDashboard';
import { useGetSalesTodayQuery, useGetSalesMonthlyQuery } from '../../services/upline';
import usePermissions from '../../hooks/usePermissions';
import './styles.scss';

/* ─── Types ──────────────────────────────────────────────────────────── */

interface SalesPerson {
  id:         string;
  name:       string;
  revenue:    number;
  image?:     string;
  branchName?: string;
}

interface SalesData {
  employees:     SalesPerson[];
  trainers:      SalesPerson[];
  totalSales:    number;
  totalTrainers: number;
}

/* ─── Constants ──────────────────────────────────────────────────────── */

const FILTER_MAP: Record<string, string> = {
  '0':   'this_month',
  '1':   'last_month',
  '3':   '3_months',
  '6':   '6_months',
  'all': 'all',
};

const EMPTY_SALES_DATA: SalesData = { employees: [], trainers: [], totalSales: 0, totalTrainers: 0 };

// react-grid-layout: same draggable/resizable-card pattern as the Super
// Admin Dashboard widgets below. WidthProvider auto-measures container width.
const ReactGridLayout = WidthProvider(GridLayout);

const CHART_DEFAULT_LAYOUT: LayoutItem[] = [
  { i: 'chart-sales-today',    x: 0, y: 0, w: 6, h: 5, minW: 3, minH: 4 },
  { i: 'chart-trainers-today', x: 6, y: 0, w: 6, h: 5, minW: 3, minH: 4 },
  { i: 'chart-sales-month',    x: 0, y: 5, w: 6, h: 5, minW: 3, minH: 4 },
  { i: 'chart-trainers-month', x: 6, y: 5, w: 6, h: 5, minW: 3, minH: 4 },
];

// Which permission gates each revenue-chart card.
const CHART_PANEL_PERMISSIONS: Record<string, string> = {
  'chart-sales-today':    'SALES_EMPLOYEE_TODAY_VIEW',
  'chart-trainers-today': 'SALES_TRAINER_TODAY_VIEW',
  'chart-sales-month':    'SALES_EMPLOYEE_MONTHLY_VIEW',
  'chart-trainers-month': 'SALES_TRAINER_MONTHLY_VIEW',
};

const CHART_LAYOUT_KEY = 'dash-revenue-charts-layout-v1';

const loadChartLayout = (): LayoutItem[] => {
  try {
    const saved = localStorage.getItem(CHART_LAYOUT_KEY);
    if (saved) {
      const parsed = JSON.parse(saved) as LayoutItem[];
      const ids = new Set(parsed.map(l => l.i));
      if (CHART_DEFAULT_LAYOUT.every(d => ids.has(d.i)) && parsed.length === CHART_DEFAULT_LAYOUT.length) {
        return parsed;
      }
    }
  } catch {
    // ignore malformed storage
  }
  return CHART_DEFAULT_LAYOUT;
};

/* ─── Helpers ────────────────────────────────────────────────────────── */

function mapSalesPerson(item: any, revenueKey: string): SalesPerson {
  return {
    id:         item.id,
    name:       item.name || item.employeeId?.name,
    revenue:    item[revenueKey] || 0,
    image:      item.photo || item.employeeId?.photo,
    branchName: item.branchName,
  };
}

function transformSalesData(apiData: any, revenueKey: string): SalesData {
  if (!apiData?.data) return EMPTY_SALES_DATA;
  const employees: SalesPerson[] = (apiData.data.employee ?? []).map((i: any) => mapSalesPerson(i, revenueKey));
  const trainers:  SalesPerson[] = (apiData.data.trainer  ?? []).map((i: any) => mapSalesPerson(i, revenueKey));
  return {
    employees,
    trainers,
    totalSales:    employees.reduce((s, e) => s + e.revenue, 0),
    totalTrainers: trainers.reduce((s, t)  => s + t.revenue, 0),
  };
}

/* ─── Component ──────────────────────────────────────────────────────── */

function Dashboard() {
  const branchId         = useSelector((state: any) => state.branch.selectedBranch);
  const selectedBranchId = typeof branchId === 'object' ? (branchId?._id || branchId?.id) : branchId;

  const { hasPermission, permissionsLoaded } = usePermissions();
  // hasPermission defaults to true while permissions are still loading, so
  // wait for permissionsLoaded too — otherwise a denied query would still
  // fire once on mount before the real (denied) permission is known.
  const canView = (perm: string) => permissionsLoaded && hasPermission(perm);

  const [selectedRange, setSelectedRange] = useState('0');
  const [chartLayout, setChartLayout] = useState<LayoutItem[]>(() => loadChartLayout());
  const [minimized, setMinimized] = useState<Record<string, boolean>>({});
  const [expandedChart, setExpandedChart] = useState<string | null>(null);

  // Below this width, dragging/resizing a 12-col grid is impractical on
  // touch and just squeezes every card — fall back to a plain stacked list.
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Trainer (coach) logins get their own dashboard — upcoming add-on expiries + this month's sales.
  let loggedInUserType: string | undefined;
  try {
    loggedInUserType = JSON.parse(localStorage.getItem('user') || '{}')?.userType;
  } catch {
    loggedInUserType = undefined;
  }
  const isTrainerLogin = loggedInUserType === 'coach';

  const currentFilter = FILTER_MAP[selectedRange] ?? 'this_month';

  const showSalesToday    = canView(CHART_PANEL_PERMISSIONS['chart-sales-today']);
  const showTrainersToday = canView(CHART_PANEL_PERMISSIONS['chart-trainers-today']);
  const showSalesMonth    = canView(CHART_PANEL_PERMISSIONS['chart-sales-month']);
  const showTrainersMonth = canView(CHART_PANEL_PERMISSIONS['chart-trainers-month']);

  const { data: salesTodayData }  = useGetSalesTodayQuery(
    (selectedBranchId ? { branchId: selectedBranchId } : undefined) as any,
    { skip: isTrainerLogin || (!showSalesToday && !showTrainersToday) }
  );
  const { data: salesMonthlyData } = useGetSalesMonthlyQuery({
    filter: currentFilter,
    ...(selectedBranchId && { branchId: selectedBranchId }),
  } as any, { skip: isTrainerLogin || (!showSalesMonth && !showTrainersMonth) });

  if (isTrainerLogin) return <TrainerDashboard />;

  const todayData:   SalesData = transformSalesData(salesTodayData,  'todaySales');
  const monthlyData: SalesData = transformSalesData(salesMonthlyData, 'thisMonthSales');

  const handleChartLayoutChange = (next: readonly LayoutItem[]) => {
    const arr = next as LayoutItem[];
    setChartLayout(arr);
    try { localStorage.setItem(CHART_LAYOUT_KEY, JSON.stringify(arr)); } catch { /* ignore */ }
  };

  // Minimizing also collapses the grid row height (down to just the header)
  // instead of only hiding the chart, so the empty space closes up too. This
  // is a transient view-state change only — NOT written to localStorage —
  // so a minimized card never reloads in a collapsed-but-expanded mismatch.
  const toggleMinimized = (id: string) => {
    const willMinimize = !minimized[id];
    setMinimized(prev => ({ ...prev, [id]: willMinimize }));
    const original = CHART_DEFAULT_LAYOUT.find(d => d.i === id);
    setChartLayout(prev => prev.map(item => item.i === id
      ? { ...item, h: willMinimize ? 1 : (original?.h ?? 5), minH: willMinimize ? 1 : (original?.minH ?? 4) }
      : item));
  };

  const CHART_MAP: Record<string, { title: string; amount: string; type: 'sales' | 'trainers'; period?: string; data: SalesPerson[]; onPeriodChange?: (v: string) => void }> = {
    'chart-sales-today': {
      title: 'Sales Today Revenue', amount: todayData.totalSales.toLocaleString('en-IN'),
      type: 'sales', data: todayData.employees,
    },
    'chart-trainers-today': {
      title: 'Trainers Today Revenue', amount: todayData.totalTrainers.toLocaleString('en-IN'),
      type: 'trainers', data: todayData.trainers,
    },
    'chart-sales-month': {
      title: 'Sales Revenue', amount: monthlyData.totalSales.toLocaleString('en-IN'),
      type: 'sales', period: selectedRange, data: monthlyData.employees,
      onPeriodChange: (v: string) => setSelectedRange(v),
    },
    'chart-trainers-month': {
      title: 'Trainers Revenue', amount: monthlyData.totalTrainers.toLocaleString('en-IN'),
      type: 'trainers', period: selectedRange, data: monthlyData.trainers,
      onPeriodChange: (v: string) => setSelectedRange(v),
    },
  };

  const visibleCharts = CHART_DEFAULT_LAYOUT.filter(item => canView(CHART_PANEL_PERMISSIONS[item.i]));
  const visibleChartIds = new Set(visibleCharts.map(item => item.i));
  const visibleChartLayout = chartLayout.filter(item => visibleChartIds.has(item.i));

  const renderChartCell = (item: LayoutItem, showDragHandle: boolean) => {
    const cfg = CHART_MAP[item.i];
    const isMin = !!minimized[item.i];
    return (
      <div key={item.i} className={`dash-chart-cell ${isMin ? 'is-minimized' : ''}`}>
        <div className="dash-chart-toolbar">
          {showDragHandle && (
            <span className="dash-chart-drag-handle" title="Drag to move">
              <DragOutlined />
            </span>
          )}
          <Button
            type="text"
            size="small"
            icon={isMin ? <DownOutlined /> : <UpOutlined />}
            title={isMin ? 'Restore' : 'Minimize'}
            onMouseDown={e => e.stopPropagation()}
            onClick={() => toggleMinimized(item.i)}
          />
          <Button
            type="text"
            size="small"
            icon={<FullscreenOutlined />}
            title="Expand"
            onMouseDown={e => e.stopPropagation()}
            onClick={() => setExpandedChart(item.i)}
          />
        </div>
        <HierarchicalChart
          title={cfg.title}
          amount={cfg.amount}
          type={cfg.type}
          period={cfg.period}
          data={cfg.data}
          onPeriodChange={cfg.onPeriodChange}
        />
      </div>
    );
  };

  return (
    <div className="dashboard-wrapper">
      <SuperAdminDashboardWidgets />

      <div className="revenue-charts-section">
        {isMobile ? (
          /* ── Phone: plain full-width stack — dragging/resizing a 12-col
             grid doesn't work on touch, so skip react-grid-layout entirely. ── */
          <div className="dash-charts-mobile">
            {visibleCharts.map(item => (
              <div key={item.i} style={{ height: Math.max(item.h * 70, 260) }}>
                {renderChartCell(item, false)}
              </div>
            ))}
          </div>
        ) : (
          <ReactGridLayout
            className="dash-charts-grid"
            layout={visibleChartLayout}
            onLayoutChange={handleChartLayoutChange}
            cols={12}
            rowHeight={64}
            margin={[16, 16]}
            containerPadding={[0, 0]}
            isDraggable
            isResizable
            resizeHandles={['se']}
            draggableHandle=".dash-chart-drag-handle"
          >
            {visibleCharts.map(item => renderChartCell(item, true))}
          </ReactGridLayout>
        )}
      </div>

      <Modal
        open={!!expandedChart}
        onCancel={() => setExpandedChart(null)}
        footer={null}
        width="80vw"
        centered
        destroyOnHidden
        title={expandedChart ? CHART_MAP[expandedChart]?.title : ''}
      >
        {expandedChart && (
          <HierarchicalChart
            title=""
            amount={CHART_MAP[expandedChart].amount}
            type={CHART_MAP[expandedChart].type}
            period={CHART_MAP[expandedChart].period}
            data={CHART_MAP[expandedChart].data}
            onPeriodChange={CHART_MAP[expandedChart].onPeriodChange}
          />
        )}
      </Modal>
    </div>
  );
}

export default Dashboard;
